import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';
import { useTenantData } from '../context/TenantDataContext';
import { useSortableData } from '../hooks/useSortableData';
import ColGrip from '../components/ColGrip';
import SortableHeader from '../components/SortableHeader';
import InfoTooltip from '../components/InfoTooltip';
import SearchInput from '../components/SearchInput';
import MoreInfo from '../components/MoreInfo';
import './styles.css';
import LoadingState from '../components/LoadingState';

export default function IntuneAppsPage() {
  const { tenant } = useTenantData();
  const [apps, setApps] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const showLoading = !apps && !error;

  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    apiFetch(`/api/intune-apps?tenantId=${tenant.tenantId}`)
      .then((r) => r.json())
      .then((result) => {
        if (cancelled) return;
        if (!Array.isArray(result.apps)) setError(result.error || 'Data ikke tilgængelig');
        else setApps(result.apps);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  const filteredApps = useMemo(() => {
    if (!apps) return [];
    if (!search.trim()) return apps;
    const q = search.trim().toLowerCase();
    return apps.filter((a) => (a.displayName || '').toLowerCase().includes(q));
  }, [apps, search]);
  const { sorted, sortKey, sortDir, toggleSort } = useSortableData(filteredApps, { key: 'displayName', dir: 'asc' });
  const requiredCount = apps ? apps.filter((a) => a.isRequired).length : 0;

  return (
    <>
      {showLoading && <LoadingState />}
      {error && <p className="state-error">{error}</p>}

      {!showLoading && apps && (
        <>
          <div className="detail-hero">
            <span className="pill pill--info detail-hero__pill">Info</span>
            <h2>{apps.length} apps registreret via Intune</h2>
            <p>Overblik over hvilke programmer der er udrullet, eller klar til at blive udrullet, til jeres medarbejderes enheder.</p>
            <MoreInfo>
              <b>Ikke det samme som "Enterprise applikationer":</b> Dette er programmer I har udrullet til
              medarbejdernes computere og telefoner via Intune — installerede apps, ikke tjenester der har fået
              adgang via login.
            </MoreInfo>
          </div>

          <div className="stat-row stat-row--flat" style={{ gridTemplateColumns: '1fr' }}>
            <div className="stat-card">
              <div className="stat-card__label">Påkrævet installation</div>
              <div className="stat-card__value">{requiredCount}</div>
            </div>
          </div>

          {apps.length === 0 ? (
            <p className="modal-section__hint">Ingen apps udrullet endnu.</p>
          ) : (
            <>
              <div className="filter-row">
                <SearchInput value={search} onChange={setSearch} placeholder="Søg app..." />
                {sorted.length !== apps.length && (
                  <span className="filter-count">Viser {sorted.length} af {apps.length}</span>
                )}
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <SortableHeader label="App" sortKey="displayName" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableHeader label="Platform" sortKey="platform" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                    <SortableHeader label="Udgiver" sortKey="publisher" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                    <th style={{ position: 'relative' }}>
                      Tildelt til
                      <InfoTooltip text={'Hvilke medarbejdere eller enheder appen er sat op til at nå. "Ikke tildelt" betyder appen findes i systemet, men ingen får den endnu — ofte en rest fra et afbrudt setup.'} />
                      <ColGrip />
                    </th>
                    <th style={{ position: 'relative' }}>
                      Krævet
                      <InfoTooltip text={'Påkrævet: installeres automatisk, medarbejderen kan ikke fravælge den. Valgfri: medarbejderen kan selv vælge at installere den. Er appen "Ikke tildelt", er dette felt tomt — der er intet at kræve, når ingen modtager den.'} />
                      <ColGrip />
                    </th>
                    <SortableHeader label="Tilføjet" sortKey="createdDateTime" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((a) => (
                    <tr key={a.id}>
                      <td className="strong">{a.displayName}</td>
                      <td>{a.platform}</td>
                      <td>{a.publisher}</td>
                      <td>
                        {a.deployedTo ? a.deployedTo : <span className="mini-flag mini-flag--neutral">Ikke tildelt</span>}
                      </td>
                      <td>
                        {!a.deployedTo ? (
                          '—'
                        ) : a.isRequired ? (
                          <span className="mini-flag mini-flag--warn">Påkrævet</span>
                        ) : (
                          <span className="mini-flag mini-flag--neutral">Valgfri</span>
                        )}
                      </td>
                      <td>{a.createdDateTime ? new Date(a.createdDateTime).toLocaleDateString('da-DK') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </>
  );
}
